import React, { useEffect, useMemo, useState } from "react";
import { CheckCircle2, CloudRain, CreditCard, IndianRupee, LockKeyhole, ReceiptText, RefreshCcw, ShoppingCart, Smartphone, TrendingUp, Truck } from "lucide-react";
import { ReportsService } from "../../../services/reports.service";
import { ReportCard, ReportPanel } from "./components/ReportCards";
import DataTable from "./components/DataTable";
import TrendBars from "./components/TrendBars";
import { money, n } from "./components/reportFormat";

const baseSelect = "h-10 rounded-xl border border-[#f2c6cc] bg-white px-3 text-xs font-bold text-slate-700 outline-none transition focus:border-[#d86a78] focus:ring-2 focus:ring-[#f7d8dc]";

const PAYMENT_METHOD_OPTIONS = [
  { value: "", label: "All methods" },
  { value: "ONLINE", label: "Online" },
  { value: "PAY_ON_DELIVERY", label: "Pay on delivery" },
];

const PAYMENT_STATUS_OPTIONS = [
  { value: "", label: "All payment status" },
  { value: "PAID", label: "Paid" },
  { value: "PENDING", label: "Pending" },
  { value: "PAYMENT_PENDING_ON_DELIVERY", label: "POD pending" },
  { value: "FAILED", label: "Failed" },
  { value: "REFUNDED", label: "Refunded" },
];

function getData(res) {
  return res?.data || res || {};
}

function FilterSelect({ label, value, onChange, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] font-black text-[#d86a78]">{label}</span>
      <select className={`${baseSelect} w-full`} value={value || ""} onChange={(e) => onChange(e.target.value)}>
        {children}
      </select>
    </label>
  );
}

export default function OrderPaymentReports({ filters, dropdowns = {} }) {
  const [localFilters, setLocalFilters] = useState({ paymentMethod: "", paymentStatus: "", gatewayPaymentStatus: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const params = useMemo(() => ({ ...filters, ...localFilters }), [filters, localFilters]);

  const patch = (key, value) => {
    setLocalFilters((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError("");

    ReportsService.orderPayments(params)
      .then((res) => {
        if (!alive) return;
        setData(getData(res));
      })
      .catch((err) => {
        if (!alive) return;
        setError(err?.message || "Payment report failed");
        setData(null);
      })
      .finally(() => alive && setLoading(false));

    return () => {
      alive = false;
    };
  }, [params]);

  const stats = data?.stats || {};
  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const paymentStatusBreakdown = Array.isArray(data?.paymentStatusBreakdown) ? data.paymentStatusBreakdown : [];
  const paymentMethodBreakdown = Array.isArray(data?.paymentMethodBreakdown) ? data.paymentMethodBreakdown : [];
  const gatewayStatusBreakdown = Array.isArray(data?.gatewayStatusBreakdown) ? data.gatewayStatusBreakdown : [];
  const trend = Array.isArray(data?.trend) ? data.trend : [];

  return (
    <div className="space-y-5">
      <div className="rounded-[18px] border border-[#f4cdd2] bg-white p-4 shadow-sm">
        <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h3 className="text-sm font-black text-slate-900">Payment, POD and charge filters</h3>
            <p className="mt-1 text-[11px] font-semibold text-slate-400">
              Uses report-service data from orders + pricing_snapshot, so rain, handling, surge, small-cart charges and pay-on-delivery status stay in one place.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setLocalFilters((p) => ({ ...p }))}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#d86a78] px-3 py-2 text-xs font-black text-white"
          >
            <RefreshCcw size={14} /> Reload
          </button>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-5">
          <FilterSelect label="Payment Method" value={localFilters.paymentMethod} onChange={(v) => patch("paymentMethod", v)}>
            {PAYMENT_METHOD_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
          </FilterSelect>
          <FilterSelect label="Payment Status" value={localFilters.paymentStatus} onChange={(v) => patch("paymentStatus", v)}>
            {PAYMENT_STATUS_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            {(dropdowns.paymentStatuses || []).filter((x) => !PAYMENT_STATUS_OPTIONS.some((opt) => opt.value === x)).map((x) => <option key={x} value={x}>{x}</option>)}
          </FilterSelect>
          <FilterSelect label="Gateway Status" value={localFilters.gatewayPaymentStatus} onChange={(v) => patch("gatewayPaymentStatus", v)}>
            <option value="">All gateway status</option>
            {(dropdowns.gatewayPaymentStatuses || ["qr_created", "captured", "failed", "refunded"]).map((x) => <option key={x} value={x}>{x}</option>)}
          </FilterSelect>
        </div>
      </div>

      {error ? (
        <div className="rounded-[14px] border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-600">
          {error}
        </div>
      ) : null}

      <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
        <ReportCard icon={IndianRupee} title="Grand Total" value={loading ? "..." : money(stats.grand_total)} />
        <ReportCard icon={Truck} title="Delivery Fee" value={loading ? "..." : money(stats.delivery_fee_total)} />
        <ReportCard icon={ReceiptText} title="Handling Charge" value={loading ? "..." : money(stats.handling_charge_total)} />
        <ReportCard icon={CloudRain} title="Rain Charge" value={loading ? "..." : money(stats.rain_charge_total)} />
        <ReportCard icon={TrendingUp} title="Surge Charge" value={loading ? "..." : money(stats.surge_charge_total)} hint={`${n(stats.surge_orders)} orders`} />
        <ReportCard icon={ShoppingCart} title="Small Cart Charge" value={loading ? "..." : money(stats.small_cart_charge_total)} hint={`${n(stats.small_cart_orders)} orders`} />
        <ReportCard icon={Smartphone} title="POD Orders" value={loading ? "..." : n(stats.pay_on_delivery_orders)} hint={money(stats.pay_on_delivery_amount)} />
        <ReportCard icon={CheckCircle2} title="POD Paid" value={loading ? "..." : n(stats.pay_on_delivery_paid_orders)} hint={money(stats.pay_on_delivery_paid_amount)} />
        <ReportCard icon={LockKeyhole} title="POD Pending" value={loading ? "..." : n(stats.pay_on_delivery_pending_orders)} hint={money(stats.pay_on_delivery_pending_amount)} />
        <ReportCard icon={CreditCard} title="QR Created" value={loading ? "..." : n(stats.qr_created_orders)} />
      </div>

      {loading ? (
        <div className="py-14 text-center font-bold text-[#d86a78]">Loading payment report...</div>
      ) : (
        <>
          <TrendBars rows={trend} field="grand_total" label="Payment amount trend" subtitle="Grand total with POD count and delivery, handling, rain, surge and small-cart charges per day/hour" />

          <div className="grid gap-5 xl:grid-cols-3">
            <ReportPanel title="Payment Status Breakdown">
              <DataTable rows={paymentStatusBreakdown} columns={["payment_status", "orders", "amount", "handling_charge", "rain_charge", "surge_charge", "small_cart_charge"]} maxHeight="max-h-[280px]" />
            </ReportPanel>
            <ReportPanel title="Payment Method Breakdown">
              <DataTable rows={paymentMethodBreakdown} columns={["payment_method", "orders", "amount", "handling_charge", "rain_charge", "surge_charge", "small_cart_charge"]} maxHeight="max-h-[280px]" />
            </ReportPanel>
            <ReportPanel title="Gateway Status Breakdown">
              <DataTable rows={gatewayStatusBreakdown} columns={["gateway_payment_status", "orders", "amount"]} maxHeight="max-h-[280px]" />
            </ReportPanel>
          </div>

          <ReportPanel title="Order Payment Rows" subtitle="POD QR, payment status and customer charge breakdown are visible here.">
            <DataTable
              rows={rows}
              columns={[
                "order_number",
                "warehouse_name",
                "status",
                "payment_method",
                "payment_status",
                "gateway_payment_status",
                "subtotal",
                "delivery_fee",
                "handling_charge",
                "rain_charge",
                "surge_charge",
                "small_cart_charge",
                "tax_amount",
                "discount_amount",
                "grand_total",
                "pay_on_delivery_status",
                "otp_locked_until_payment",
                "qr_status",
                "razorpay_payment_id",
                "payment_verified_at",
                "created_at",
              ]}
            />
          </ReportPanel>
        </>
      )}
    </div>
  );
}
